import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, CheckCircle, Star, Heart, Shield, Sparkles, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AppointmentForm.scss';
import { getAppointmentServices, bookAppointment } from '../../api/api';

const trackAppointmentFormSubmitted = () => console.log('Analytics tracked');

const initialFormState = {
  name: "",
  email: "",
  service: "",
  date: "",
  time: "",
  contact_no: "",
  visited_before: "no",
  gender: "M",
  message: "",
};

const ModernDentalAppointment = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [minTime, setMinTime] = useState("");
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [clinicTiming, setClinicTiming] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [storedFormData, setStoredFormData] = useState(null);

  // Get next available date (tomorrow if today is past clinic hours, otherwise today)
  const getNextAvailableDate = () => {
    const today = new Date();
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    
    // If it's past 5 PM, default to tomorrow
    if (currentHour >= 17) {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    }
    
    return today.toISOString().split('T')[0];
  };

  // Get next available time slot
  const getNextAvailableTime = () => {
    const currentTime = new Date();
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    
    // Round up to next 30-minute slot
    let nextHour = currentHour;
    let nextMinute = currentMinute < 30 ? 30 : 0;
    
    if (nextMinute === 0) {
      nextHour += 1;
    }
    
    // Ensure it's within clinic hours (9 AM - 6 PM)
    if (nextHour < 9) {
      nextHour = 9;
      nextMinute = 0;
    } else if (nextHour >= 18) {
      // If past 6 PM, default to 9 AM tomorrow
      return "09:00";
    }
    
    return `${nextHour.toString().padStart(2, '0')}:${nextMinute.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setServicesLoading(true);
        const apiData = await getAppointmentServices();
        
        if (apiData && apiData.services && apiData.services.length > 0) {
          // Transform API data to match frontend format
          const transformedServices = apiData.services.map((service, index) => ({
            id: index + 1,
            name: service.name,
            description: service.description,
            // icon: "🦷", // Default icon since API doesn't provide one
            duration: service.duration_display || "60 min", // Use API duration or fallback
            popular: index < 3, // Mark first 3 as popular
          }));
          setServices(transformedServices);
          
          // Set clinic timing information
          if (apiData.clinic_timing) {
            setClinicTiming(apiData.clinic_timing);
            
            // Generate time slots based on clinic timing
            const slots = generateTimeSlotsFromClinicTiming(apiData.clinic_timing);
            setTimeSlots(slots);
          }
        } else {
          // Fallback to default services if API returns empty
          setServices([
            { id: 1, name: "General Checkup", icon: "🦷", duration: "30 min", popular: true },
            { id: 2, name: "Teeth Whitening", icon: "✨", duration: "60 min", popular: true },
            { id: 3, name: "Dental Cleaning", icon: "🧽", duration: "45 min", popular: false },
          ]);
          
          // Set default time slots
          setTimeSlots([
            "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
            "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
            "16:00", "16:30", "17:00", "17:30", "18:00"
          ]);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback to default services on error
        setServices([
          { id: 1, name: "General Checkup", icon: "🦷", duration: "30 min", popular: true },
          { id: 2, name: "Teeth Whitening", icon: "✨", duration: "60 min", popular: true },
          { id: 3, name: "Dental Cleaning", icon: "🧽", duration: "45 min", popular: false },
        ]);
        
        // Set default time slots
        setTimeSlots([
          "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
          "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
          "16:00", "16:30", "17:00", "17:30", "18:00"
        ]);
      } finally {
        setServicesLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Set default date and time when component mounts
  useEffect(() => {
    if (!formData.date) {
      setFormData(prev => ({
        ...prev,
        date: getNextAvailableDate(),
        time: getNextAvailableTime()
      }));
    }
  }, []);

  // Scroll to top when success page is shown
  useEffect(() => {
    if (showSuccess) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [showSuccess]);

  // Function to generate time slots from clinic timing
  const generateTimeSlotsFromClinicTiming = (timing) => {
    const slots = [];
    
    if (timing.morning_start_time && timing.morning_end_time) {
      const morningStart = timing.morning_start_time;
      const morningEnd = timing.morning_end_time;
      
      // Generate 30-minute slots for morning session
      let currentTime = new Date(`2000-01-01T${morningStart}`);
      const endTime = new Date(`2000-01-01T${morningEnd}`);
      
      while (currentTime < endTime) {
        slots.push(currentTime.toTimeString().slice(0, 5));
        currentTime.setMinutes(currentTime.getMinutes() + 30);
      }
    }
    
    if (timing.evening_start_time && timing.evening_end_time) {
      const eveningStart = timing.evening_start_time;
      const eveningEnd = timing.evening_end_time;
      
      // Generate 30-minute slots for evening session
      let currentTime = new Date(`2000-01-01T${eveningStart}`);
      const endTime = new Date(`2000-01-01T${eveningEnd}`);
      
      while (currentTime < endTime) {
        slots.push(currentTime.toTimeString().slice(0, 5));
        currentTime.setMinutes(currentTime.getMinutes() + 30);
      }
    }
    
    // If no timing data, return default slots
    if (slots.length === 0) {
      return [
        "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
        "12:00", "12:30", "14:00", "14:30", "15:00", "15:30",
        "16:00", "16:30", "17:00", "17:30", "18:00"
      ];
    }
    
    return slots.sort();
  };

  // Function to get clinic timing display string
  const getClinicTimingDisplay = () => {
    if (clinicTiming.morning_start_time && clinicTiming.morning_end_time && 
        clinicTiming.evening_start_time && clinicTiming.evening_end_time) {
      const morningStart = clinicTiming.morning_start_time;
      const eveningEnd = clinicTiming.evening_end_time;
      return `🕒 ${morningStart} - ${eveningEnd}`;
    } else if (clinicTiming.morning_start_time && clinicTiming.morning_end_time) {
      return `🕒 ${clinicTiming.morning_start_time} - ${clinicTiming.morning_end_time}`;
    } else if (clinicTiming.evening_start_time && clinicTiming.evening_end_time) {
      return `🕒 ${clinicTiming.evening_start_time} - ${clinicTiming.evening_end_time}`;
    }
    return "🕒 9:00 AM - 6:00 PM"; // Default fallback
  };

  // Function to validate time slot against clinic timing
  const validateTimeSlot = (time) => {
    if (!clinicTiming.morning_start_time && !clinicTiming.evening_start_time) {
      // Fallback validation if no clinic timing data
      if (time < "09:00" || time > "18:00") {
        return 'Appointment time must be between 09:00 and 18:00';
      }
      return true;
    }

    // Check if time falls within morning session
    if (clinicTiming.morning_start_time && clinicTiming.morning_end_time) {
      if (time >= clinicTiming.morning_start_time && time <= clinicTiming.morning_end_time) {
        return true;
      }
    }

    // Check if time falls within evening session
    if (clinicTiming.evening_start_time && clinicTiming.evening_end_time) {
      if (time >= clinicTiming.evening_start_time && time <= clinicTiming.evening_end_time) {
        return true;
      }
    }

    // If we have clinic timing but time doesn't fall within any session
    if (clinicTiming.morning_start_time || clinicTiming.evening_start_time) {
      let validRange = '';
      if (clinicTiming.morning_start_time && clinicTiming.evening_end_time) {
        validRange = `${clinicTiming.morning_start_time} and ${clinicTiming.evening_end_time}`;
      } else if (clinicTiming.morning_start_time && clinicTiming.morning_end_time) {
        validRange = `${clinicTiming.morning_start_time} and ${clinicTiming.morning_end_time}`;
      } else if (clinicTiming.evening_start_time && clinicTiming.evening_end_time) {
        validRange = `${clinicTiming.evening_start_time} and ${clinicTiming.evening_end_time}`;
      }
      return `Appointment time must be within clinic hours: ${validRange}`;
    }

    return true;
  };

  const getToday = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  useEffect(() => {
    if (formData.date === getToday()) {
      const currentTime = getCurrentTime();
      // Use clinic timing if available, otherwise fallback to 9:00 AM
      const defaultStartTime = clinicTiming.morning_start_time || "09:00";
      setMinTime(currentTime < defaultStartTime ? defaultStartTime : currentTime);
    } else {
      // Use clinic timing if available, otherwise fallback to 9:00 AM
      setMinTime(clinicTiming.morning_start_time || "09:00");
    }
  }, [formData.date, clinicTiming.morning_start_time]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "radio" ? value : type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.contact_no) newErrors.contact_no = 'Contact number is required';
    if (!formData.service) newErrors.service = 'Please select a service';
    if (!formData.date) newErrors.date = 'Please select a date';
    if (!formData.time) newErrors.time = 'Please select a time';

    // Email validation (only if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone number validation
    if (formData.contact_no && !/^\d{10}$/.test(formData.contact_no)) {
      newErrors.contact_no = 'Please enter a valid 10-digit phone number';
    }

    if (formData.date && formData.date < getToday()) {
      newErrors.date = 'Please select today or a future date';
    }

    if (formData.date === getToday() && formData.time && formData.time <= getCurrentTime()) {
      newErrors.time = 'Please select a future time';
    }

    // Time validation using dynamic clinic timing if available
    if (formData.time) {
      const timeValidation = validateTimeSlot(formData.time);
      if (timeValidation !== true) {
        newErrors.time = timeValidation;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Smooth scroll to first error
      const firstErrorField = document.querySelector('.error-field');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      // Prepare appointment data for API
      const appointmentData = {
        name: formData.name,
        contact_no: formData.contact_no,
        date: formData.date,
        time: formData.time,
        service: formData.service,
        message: formData.message || '', // Handle optional message
        gender: formData.gender,
        visited_before: formData.visited_before === 'yes',
        department: 'Dental', // Default department
      };

      // Only add email if it has a value
      if (formData.email && formData.email.trim()) {
        appointmentData.email = formData.email.trim();
      }

      // Call appointment booking API
      const result = await bookAppointment(appointmentData);

      if (result.success) {
        trackAppointmentFormSubmitted();
        
        // Store the current form data before resetting it
        const currentFormData = { ...formData };
        
        setShowSuccess(true);
        setAppointmentDetails(result.data || result.appointment); // Try both possible response structures
        setStoredFormData(currentFormData); // Store form data for success message
        setFormData(initialFormState); // Reset form data
        
        // Debug: Log what we received
        console.log('API Response:', result);
        console.log('Stored Form Data:', currentFormData);
        console.log('Appointment Details:', result.data || result.appointment);
        
        // Remove auto-redirect timeout - let users stay as long as they want
        // setTimeout(() => setShowSuccess(false), 6000);
        
        // Scroll to top to show the success message properly
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Handle API errors
        let errorMessage = 'Failed to book appointment. Please try again.';
        
        if (result.message) {
          errorMessage = result.message;
        } else if (result.errors) {
          // Handle validation errors from backend
          const firstError = Object.values(result.errors)[0];
          if (Array.isArray(firstError)) {
            errorMessage = firstError[0];
          } else {
            errorMessage = firstError;
          }
        }
        
        setErrors(prev => ({ ...prev, submit: errorMessage }));
        console.error('Booking error:', result);
      }
    } catch (error) {
      console.error('Network error:', error);
      setErrors(prev => ({ ...prev, submit: 'Network error. Please check your connection and try again.' }));
    } finally {
      setLoading(false);
    }
  };

  const getAvailableTimeSlots = () => {
    if (formData.date === getToday()) {
      const currentTime = getCurrentTime();
      return timeSlots.filter(slot => slot > currentTime);
    }
    return timeSlots;
  };

  if (showSuccess) {
    return (
      <div className="success-container">
        <div className="success-card">
          <div className="success-icon">
            <CheckCircle size={48} />
          </div>
          <h2 className="success-title">🎉 Appointment Booked Successfully!</h2>
          <p className="success-message">
            Thank you for choosing us! Your appointment has been confirmed and we're looking forward to taking care of your smile.
          </p>
          {/* <p className="success-note">
            💡 You can stay on this page as long as you need to review the details below.
          </p> */}
          
          {/* Appointment Details */}
          <div className="appointment-details">
            <h3>Appointment Details</h3>
            <div className="detail-row">
              <div className="detail-item">
                <Calendar size={16} />
                <span><strong>Date:</strong> {
                  (() => {
                    try {
                      // Try to get date from appointment details first
                      if (appointmentDetails?.date) {
                        const date = new Date(appointmentDetails.date);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          });
                        }
                      }
                      // Fallback to stored form data
                      if (storedFormData?.date) {
                        const date = new Date(storedFormData.date);
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          });
                        }
                      }
                      // If all else fails, show a message
                      return 'Date not available';
                    } catch (error) {
                      return 'Date not available';
                    }
                  })()
                }</span>
              </div>
              <div className="detail-item">
                <Clock size={16} />
                <span><strong>Time:</strong> {appointmentDetails?.time || storedFormData?.time || 'Time not available'}</span>
              </div>
            </div>
            <div className="detail-row">
              <div className="detail-item">
                <User size={16} />
                <span><strong>Service:</strong> {appointmentDetails?.service || storedFormData?.service || 'Service not available'}</span>
              </div>
              <div className="detail-item">
                <Phone size={16} />
                <span><strong>Contact:</strong> {appointmentDetails?.contact_no || storedFormData?.contact_no || 'Contact not available'}</span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="next-steps">
            <h3>What Happens Next?</h3>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Confirmation Email</strong>
                  <span>We've sent a confirmation email with all the details</span>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>SMS Reminder</strong>
                  <span>You'll receive an SMS reminder 24 hours before your appointment</span>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Arrival</strong>
                  <span>Please arrive 15 minutes early for your appointment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="important-notes">
            <h3>Important Notes</h3>
            <ul>
              <li>Please bring a valid ID and any relevant medical records</li>
              <li>If you need to reschedule, please call us at least 24 hours in advance</li>
              <li>For any questions, feel free to contact us</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="success-actions">
            <button 
              onClick={() => {
                setShowSuccess(false);
                setAppointmentDetails(null);
                setStoredFormData(null);
                setFormData(initialFormState);
                // Reset to default date and time
                setFormData(prev => ({
                  ...prev,
                  date: getNextAvailableDate(),
                  time: getNextAvailableTime()
                }));
              }}
              className="book-another-btn"
            >
              📅 Book Another Appointment
            </button>
            <button 
              onClick={() => {
                // Redirect to home page
                navigate('/');
              }}
              className="home-btn"
            >
              🏠 Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appointment-container">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="header-info">
            <div className="logo">
              <Heart size={24} />
            </div>
            <div className="header-text">
              <h1>Book Your Dental Appointment</h1>
              <p>Quick, easy, and secure booking in under 2 minutes</p>
            </div>
          </div>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">

          {/* Personal Information */}
          <div className="section">
            <div className="section-header">
              <User size={24} />
              <h2>Personal Information</h2>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  className={`form-input ${errors.name ? 'error-field' : ''} ${focusedField === 'name' ? 'focused' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="error-message">⚠️ {errors.name}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="contact_no">Contact Number *</label>
                <input
                  id="contact_no"
                  type="tel"
                  name="contact_no"
                  value={formData.contact_no}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('contact_no')}
                  onBlur={() => setFocusedField(null)}
                  className={`form-input ${errors.contact_no ? 'error-field' : ''} ${focusedField === 'contact_no' ? 'focused' : ''}`}
                  placeholder="1234567890"
                  pattern="[0-9]{10}"
                  maxLength="10"
                  onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                />
                {errors.contact_no && <p className="error-message">⚠️ {errors.contact_no}</p>}
              </div>
            </div>

            {/* Advanced Fields Toggle */}
            <div className="advanced-fields-toggle">
              <button
                type="button"
                onClick={() => setShowAdvancedFields(!showAdvancedFields)}
                className="toggle-button"
              >
                <span>Additional Information</span>
                {showAdvancedFields ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </button>
            </div>

            {/* Advanced Fields (Hidden by default on mobile) */}
            {showAdvancedFields && (
              <div className="advanced-fields">
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="email">Email Address</label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('email')}
                      onBlur={() => setFocusedField(null)}
                      className={`form-input ${errors.email ? 'error-field' : ''} ${focusedField === 'email' ? 'focused' : ''}`}
                      placeholder="your.email@example.com"
                    />
                    {errors.email && <p className="error-message">⚠️ {errors.email}</p>}
                  </div>

                  <div className="form-group">
                    <label>Gender</label>
                    <div className="radio-group">
                      {[
                        { value: "M", label: "Male" },
                        { value: "F", label: "Female" },
                        { value: "O", label: "Other" }
                      ].map((option) => (
                        <label key={option.value} className={`radio-option ${formData.gender === option.value ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="gender"
                            value={option.value}
                            checked={formData.gender === option.value}
                            onChange={handleChange}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Optional Message */}
                  <div className="form-group">
                    <label htmlFor="message">
                      <MessageSquare size={16} style={{ display: 'inline', marginRight: '8px' }} />
                      Additional Notes <span className="optional">(Optional)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="3"
                      className="form-textarea"
                      placeholder="Any specific concerns, preferences, or questions? (e.g., 'I have dental anxiety', 'Preferred dentist', etc.)"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Service Selection */}
          <div className="section service-section">
            <div className="section-header">
              <Sparkles size={24} />
              <h2>Choose Your Service</h2>
              {errors.service && <span className="error-inline">⚠️ Please select a service</span>}
            </div>

            {servicesLoading ? (
              <div className="services-loading">
                <div className="loading-spinner" />
                <p>Loading available services...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="services-error">
                <p>No services available at the moment.</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="retry-button"
                >
                  Retry
                </button>
              </div>
            ) : (
              <div className="services-grid">
                {services.map((service) => (
                  <div
                    key={service.id}
                    onClick={() => {
                      setFormData(prev => ({ ...prev, service: service.name }));
                      // Clear service error when user selects a service
                      if (errors.service) {
                        setErrors(prev => ({ ...prev, service: '' }));
                      }
                    }}
                    className={`service-card ${formData.service === service.name ? 'selected' : ''}`}
                  >
                    {service.popular && (
                      <div className="popular-badge">
                        Popular
                      </div>
                    )}
                    <div className="service-content">
                      <div className="service-icon">{service.icon}</div>
                      <h3>{service.name}</h3>
                      <p>{service.duration}</p>
                      {formData.service === service.name && (
                        <CheckCircle className="selected-icon" size={20} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date & Time Selection */}
          <div className="section datetime-section">
            <div className="section-header">
              <Calendar size={24} />
              <h2>Select Date & Time</h2>
            </div>

            <div className="datetime-grid">
              {/* Date Field */}
              <div className="form-group">
                <label htmlFor="date">Preferred Date *</label>
                <input
                  id="date"
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('date')}
                  onBlur={() => setFocusedField(null)}
                  min={getToday()}
                  className={`form-input date-input ${errors.date ? 'error-field' : ''} ${focusedField === 'date' ? 'focused' : ''}`}
                />
                {errors.date && <p className="error-message">⚠️ {errors.date}</p>}
              </div>

              {/* Time Slots */}
              <div className="form-group">
                <label>
                  Available Time Slots *
                  {/* <span className="time-info">{getClinicTimingDisplay()}</span> */}
                </label>
                <div className="time-slots">
                  {getAvailableTimeSlots().map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, time }))}
                      className={`time-slot ${formData.time === time ? 'selected' : ''}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
                {errors.time && <p className="error-message">⚠️ {errors.time}</p>}
              </div>
            </div>

            {/* Clinic Information */}
            {(clinicTiming.working_days || clinicTiming.timing_note) && (
              <div className="clinic-info">
                {clinicTiming.working_days && (
                  <div className="info-item">
                    <Calendar size={16} />
                    <span><strong>Working Days:</strong> {clinicTiming.working_days}</span>
                  </div>
                )}
                {clinicTiming.timing_note && (
                  <div className="info-item">
                    <MessageSquare size={16} />
                    <span><strong>Note:</strong> {clinicTiming.timing_note}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Returning Patient Question */}
          {/* <div className="section visit-status-section">
            <div className="section-header">
              <h2>Visit Status</h2>
            </div>
            <div className="form-group">
              <label>Have you visited us before? *</label>
              <div className="radio-group">
                {[
                  { value: "yes", label: "Yes, I'm a returning patient" },
                  { value: "no", label: "No, this is my first visit" }
                ].map((option) => (
                  <label key={option.value} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <input
                      type="radio"
                      name="visited_before"
                      value={option.value}
                      checked={formData.visited_before === option.value}
                      onChange={handleChange}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          </div> */}


          {/* Submit Button */}
          <div className="section submit-section">
            {errors.submit && (
              <div className="error-message submit-error">
                ⚠️ {errors.submit}
              </div>
            )}
            <button
              onClick={handleSubmit}
              disabled={loading || servicesLoading}
              className="submit-button"
            >
              {loading && <div className="loading-spinner" />}
              <span>
                {loading 
                  ? 'Booking Your Appointment...' 
                  : servicesLoading 
                    ? 'Loading Services...' 
                    : '🎯 Confirm My Appointment'
                }
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernDentalAppointment;